// Cloud bridge — mirrors every `cloud:*` / `project:*` / `draft:*`
// IPC channel from src/main/preload.js using the REST client +
// browser storage. The renderer's `doc-store.js` is what actually
// owns the Y.Doc + Hocuspocus connection; this bridge supplies the
// REST surface around it (project list, draft CRUD, snapshot labels,
// collab-token mint) plus all the local caches Electron stores in
// userData but web stores in localStorage.

import { createCloudClient } from '../cloud/client.js';

const LS_KEY_CLOUD_LIST = 'lastslate:cloud:list-cache';
const LS_KEY_DRAFTS_PREFIX = 'lastslate:cloud:drafts:';
const LS_KEY_LOCAL_BINDINGS = 'lastslate:cloud:local-bindings';

function lsGet(key, fallback = null) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}
function lsSet(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}
function lsRemove(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* empty */
  }
}

function b64encode(bytes) {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let bin = '';
  for (const b of arr) bin += String.fromCharCode(b);
  return btoa(bin);
}
function b64decode(s) {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function buildCloudHandlers({ supabase, env, bus }) {
  const client = createCloudClient({ supabase, env });

  function collabBaseUrl() {
    return env?.COLLAB_URL || '';
  }
  function wsUrl() {
    const http = collabBaseUrl().replace(/\/$/, '');
    return http.replace(/^http/, 'ws') + '/collab';
  }

  return {
    // ── Identity / config ─────────────────────────────────────────
    async cloudBaseUrl() {
      return { ok: true, url: collabBaseUrl() };
    },

    async cloudGetCollabToken({ projectId, draftId } = {}) {
      if (!supabase) return { ok: false, error: 'cloud_not_configured' };
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (!token) return { ok: false, error: 'not_signed_in' };
      const base = { ok: true, token, url: wsUrl() };
      if (projectId && draftId) {
        base.room = `project:${projectId}:draft:${draftId}`;
      }
      return base;
    },

    // ── Project list (with stale-while-revalidate localStorage cache) ──
    async cloudListProjects() {
      const r = await client.listProjects();
      if (r.ok && Array.isArray(r.projects)) {
        // Mirror the Electron envelope shape the renderer expects:
        // { ok, projects: [{ cloudId, name, format, updatedAt, ...}] }
        const projects = r.projects.map((p) => ({
          cloudId: p.id,
          name: p.title,
          format: p.format,
          role: p.role,
          createdAt: p.created_at,
          updatedAt: p.updated_at,
        }));
        // Keep the local cache fresh so the welcome screen can render
        // instantly on next load while the network call is in flight.
        lsSet(LS_KEY_CLOUD_LIST, { projects, cachedAt: new Date().toISOString() });
        return { ok: true, projects };
      }
      // Free-tier users get HTTP 402 `free_tier_blocked` from the
      // collab server. Soften that into an empty list so the renderer
      // shows the normal "no cloud projects" state instead of an
      // error banner — matches Electron-free behavior where the
      // welcome just shows local recents and no cloud section.
      if (!r.ok && (r.error === 'free_tier_blocked' || r.status === 402)) {
        return { ok: true, projects: [], tier: 'free' };
      }
      return r;
    },

    async cloudListCacheRead() {
      const cached = lsGet(LS_KEY_CLOUD_LIST);
      if (!cached || !Array.isArray(cached.projects)) {
        return { ok: true, projects: [], cachedAt: null };
      }
      return { ok: true, ...cached };
    },
    async cloudListCacheWrite({ projects } = {}) {
      lsSet(LS_KEY_CLOUD_LIST, {
        projects: projects || [],
        cachedAt: new Date().toISOString(),
      });
      return { ok: true };
    },
    async cloudListCacheClear() {
      lsRemove(LS_KEY_CLOUD_LIST);
      return { ok: true };
    },

    // ── Project create / open / archive ──────────────────────────
    async cloudCreateProject({ title, format = 'feature' } = {}) {
      const r = await client.createProject({ title, format });
      if (r.ok && r.project) {
        return {
          ok: true,
          project: {
            cloudId: r.project.id,
            name: r.project.title,
            format: r.project.format,
            createdAt: r.project.created_at,
            updatedAt: r.project.updated_at,
          },
          mainDraft: r.draft
            ? {
                draftId: r.draft.id,
                name: r.draft.name,
                displayName: r.draft.display_name,
              }
            : null,
        };
      }
      return r;
    },

    async cloudArchiveProject({ cloudId } = {}) {
      if (!cloudId) return { ok: false, error: 'cloudId required' };
      return client.archiveProject(cloudId);
    },

    async cloudOpenProject({ cloudId } = {}) {
      if (!cloudId) return { ok: false, error: 'cloudId required' };
      const r = await client.getProject(cloudId);
      if (!r.ok) return r;
      // Renderer expects { ok, project, drafts } in the same envelope.
      const project = r.project
        ? {
            cloudId: r.project.id,
            name: r.project.title,
            format: r.project.format,
            createdAt: r.project.created_at,
            updatedAt: r.project.updated_at,
          }
        : null;
      const drafts = Array.isArray(r.drafts)
        ? r.drafts.map((d) => ({
            draftId: d.id,
            name: d.name,
            displayName: d.display_name || d.name,
            createdAt: d.created_at,
            updatedAt: d.updated_at,
          }))
        : [];
      return { ok: true, project, drafts };
    },

    // ── Drafts cache (per-project list) ──────────────────────────
    async cloudSaveDraftsCache({ cloudId, drafts } = {}) {
      if (!cloudId) return { ok: false, error: 'cloudId required' };
      lsSet(`${LS_KEY_DRAFTS_PREFIX}${cloudId}`, drafts || []);
      return { ok: true };
    },
    async cloudLoadDraftsCache({ cloudId } = {}) {
      if (!cloudId) return { ok: false, error: 'cloudId required' };
      const drafts = lsGet(`${LS_KEY_DRAFTS_PREFIX}${cloudId}`, []) || [];
      return { ok: true, drafts };
    },

    // ── Draft create / rename / archive / snapshots ──────────────
    async cloudDraftCreate({ cloudId, name, fromDraft, atSeq } = {}) {
      if (!cloudId) return { ok: false, error: 'cloudId required' };
      const r = await client.createDraft(cloudId, { name, fromDraft, atSeq });
      if (r.ok && r.draft) {
        return {
          ok: true,
          draft: {
            draftId: r.draft.id,
            name: r.draft.name,
            displayName: r.draft.display_name || r.draft.name,
            createdAt: r.draft.created_at,
          },
        };
      }
      return r;
    },
    async cloudDraftRename({ draftId, name } = {}) {
      if (!draftId || !name) return { ok: false, error: 'draftId+name required' };
      return client.renameDraft(draftId, name);
    },
    async cloudDraftArchive({ draftId } = {}) {
      if (!draftId) return { ok: false, error: 'draftId required' };
      return client.archiveDraft(draftId);
    },
    async cloudLabelSnapshot({ draftId, label } = {}) {
      if (!draftId || !label) return { ok: false, error: 'draftId+label required' };
      return client.labelSnapshot(draftId, label);
    },

    // ── Draft state (raw Y.Doc bytes via REST, only used by the
    //    `Make a copy / Save a local copy` fall-back path; the live
    //    sync path uses Hocuspocus directly via the renderer's
    //    doc-store). ──────────────────────────────────────────────
    async cloudLoadDraftState(draftId) {
      const r = await client.loadDraftState(draftId);
      if (!r.ok) return r;
      const bytes = r.bytes ? b64decode(r.bytes) : null;
      return { ok: true, bytes };
    },
    async cloudSaveDraftState(draftId, bytes) {
      if (!draftId) return { ok: false, error: 'draftId required' };
      if (!bytes || !bytes.length) return { ok: false, error: 'non-empty bytes required' };
      return client.saveDraftState(draftId, b64encode(bytes));
    },

    // ── Local-binding shims (Electron stores user-chosen disk paths
    //    in path-map; on web, the concept of "the local .fountain
    //    file on disk" doesn't apply — these stubs preserve the
    //    renderer contract while no-oping). ─────────────────────
    async cloudBindLocalPath({ cloudId, filePath } = {}) {
      if (!cloudId) return { ok: false, error: 'cloudId required' };
      const bindings = lsGet(LS_KEY_LOCAL_BINDINGS, {}) || {};
      bindings[cloudId] = { filePath: filePath || null, boundAt: new Date().toISOString() };
      lsSet(LS_KEY_LOCAL_BINDINGS, bindings);
      return { ok: true };
    },
    async cloudClearLocalBinding({ cloudId } = {}) {
      if (!cloudId) return { ok: false, error: 'cloudId required' };
      const bindings = lsGet(LS_KEY_LOCAL_BINDINGS, {}) || {};
      delete bindings[cloudId];
      lsSet(LS_KEY_LOCAL_BINDINGS, bindings);
      return { ok: true };
    },
    async cloudListLocalBindings() {
      const bindings = lsGet(LS_KEY_LOCAL_BINDINGS, {}) || {};
      return { ok: true, bindings };
    },
    async cloudCheckLocalCopy(/* { cloudId } */) {
      // Web has no local-disk copy concept. Always returns "no copy".
      return { ok: true, exists: false };
    },
    async cloudSaveLocalCopyAs({ cloudId, draftId, content, suggestedName } = {}) {
      // Browser equivalent of "Save a local copy" — download the
      // Fountain text as a file. Bypasses the OS Save dialog (no
      // such thing in the browser without the File System Access
      // API which isn't universal yet). Returns the suggested name so
      // the renderer's status banner can display it.
      if (!content) return { ok: false, error: 'content required' };
      const name = suggestedName || `${cloudId || 'screenplay'}.fountain`;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return { ok: true, filePath: name, draftId };
    },

    async cloudLocalPath() {
      // No filesystem path on web; renderer can use the bindings table
      // for a displayable label or fall back to null.
      return { ok: true, filePath: null };
    },
  };
}
